import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Leaf } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { trackEvent } from '../utils/analytics';

export default function WhatsAppWidget() {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [hasPrompted, setHasPrompted] = useState(false);
	const [inputText, setInputText] = useState('');

	// Auto-prompt user with a subtle notification sound/appearance after 5 seconds
	useEffect(() => {
		const timer = setTimeout(() => {
			setHasPrompted(true);
		}, 5000);
		return () => clearTimeout(timer);
	}, []);

	// WhatsApp click handler
	const handleStartChat = (e?: React.FormEvent) => {
		if (e) e.preventDefault();

		trackEvent('click_whatsapp', { category: 'whatsapp_widget' });

		// Default or user typed message text
		const message = inputText.trim()
			? encodeURIComponent(inputText)
			: encodeURIComponent(
					'Hello! I am exploring your majestic curations and would love to design a bespoke African expedition.',
				);


		// Configurable phone number (using a premium format for representation)
		const phoneNumber = '41765456616'; // Standard high-end UK/Int concierge number representation
		const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

		window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
		setIsOpen(false);
	};

	return (
		<div
			id='whatsapp-integration-widget'
			className='fixed bottom-20 sm:bottom-24 right-5 sm:right-7 z-50 font-sans text-left'
		>
			{/* 1. Pulser alert dot for new message context */}
			{!isOpen && !hasPrompted && (
				<span className='absolute -top-1 -right-1 flex h-3 w-3 z-50'>
					<span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-terracotta opacity-75'></span>
					<span className='relative inline-flex rounded-full h-3 w-3 bg-brand-terracotta'></span>
				</span>
			)}

			{/* 2. Magnetic swell action button button */}
			<motion.button
				id='whatsapp-trigger-btn'
				onClick={() => {
					setIsOpen(!isOpen);
					setHasPrompted(true);
				}}
				whileHover={{ scale: 1.08 }}
				whileTap={{ scale: 0.94 }}
				className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl cursor-pointer border transition-all duration-300 ${
					isOpen
						? 'bg-white border-earth-sand text-brand-forest hover:bg-earth-cream'
						: 'bg-emerald-600 hover:bg-emerald-500 border-emerald-500 text-white'
				}`}
				title={t('directWhatsAppInquire')}
			>
				{isOpen ? (
					<X className='w-6 h-6 animate-fade-in' />
				) : (
					<div className='relative'>
						<svg viewBox='0 0 24 24' fill='currentColor' className='w-7 h-7'>
							<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
						</svg>
					</div>
				)}
			</motion.button>

			{/* 3. Luxury Chat Popover Window */}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0, y: 15, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 15, scale: 0.95 }}
						className='absolute bottom-18 right-0 w-80 sm:w-92 bg-white rounded-2xl shadow-2xl border border-earth-sand/30 overflow-hidden flex flex-col'
					>
						{/* Header: Forest green brand block */}
						<div className='bg-brand-forest-dark p-4 text-white flex items-center justify-between'>
							<div className='flex items-center space-x-3'>
								<div className='relative'>
									<div className='w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center border border-brand-gold'>
										<Leaf className='w-5 h-5 text-brand-gold' />
									</div>
									<span className='absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border border-brand-forest-dark'></span>
								</div>
								<div>
									<p className='font-sans text-[10px] text-brand-gold-light/95 uppercase tracking-widest font-medium'>
										{t('leadSafariPlanner')}
									</p>
								</div>
							</div>
							<button
								onClick={() => setIsOpen(false)}
								className='text-white/70 hover:text-white transition-colors cursor-pointer'
							>
								<X className='w-5 h-5' />
							</button>
						</div>

						{/* Conversation Flow Display */}
						<div className='p-4 bg-earth-cream/40 space-y-4 max-h-47.5 overflow-y-auto'>
							{/* System Notice */}
							<div className='text-center'>
								<span className='font-mono text-[8px] bg-earth-sand/35 text-brand-charcoal-light py-0.5 px-2 rounded-full uppercase tracking-widest font-extrabold'>
									{t('sovereignEncryptionSecure')}
								</span>
							</div>

							{/* Coordinator Message Grid */}
							<div className='flex items-start space-x-2.5 text-left'>
								<div className='w-7 h-7 rounded-full bg-brand-forest flex items-center justify-center text-brand-gold text-[10px] font-bold shrink-0 mt-0.5'>
									SR
								</div>
								<div className='bg-white border border-earth-sand/15 p-3 rounded-r-xl rounded-bl-xl shadow-xs max-w-[85%]'>
									<p className='font-sans text-[11.5px] leading-relaxed text-brand-charcoal font-light'>
										{t('welcomeMessage')}
									</p>
									<span className='font-mono text-[8px] text-brand-charcoal-light/65 mt-1 block'>
										{t('typicallyReplies')}
									</span>
								</div>
							</div>
						</div>

						{/* Custom Interactive Message Input Area */}
						<form
							onSubmit={handleStartChat}
							className='p-3 bg-white border-t border-earth-sand/25 flex items-center space-x-2'
						>
							<input
								type='text'
								placeholder={t('typeBespokeQuestDetail')}
								value={inputText}
								onChange={e => setInputText(e.target.value)}
								className='flex-1 bg-earth-cream text-brand-charcoal border border-earth-sand/35 rounded-xl px-3 py-2.5 text-xs font-sans placeholder:text-brand-charcoal-light focus:outline-none focus:border-brand-forest transition-colors'
							/>
							<button
								type='submit'
								className='h-9 w-9 bg-brand-forest hover:bg-brand-forest-light text-brand-gold rounded-xl flex items-center justify-center cursor-pointer transition-colors duration-300'
								title='Send via WhatsApp'
							>
								<Send className='w-4 h-4' />
							</button>
						</form>

						{/* Flat Direct Fast Action Button */}
						<div className='px-3 pb-3 bg-white'>
							<button
								type='button'
								onClick={() => handleStartChat()}
								className='w-full bg-emerald-600 hover:bg-emerald-500 text-white font-sans text-[10px] tracking-widest uppercase font-bold py-2 px-4 rounded-xl cursor-pointer transition-colors flex items-center justify-center space-x-1.5 shadow-sm'
							>
								<span>{t('directWhatsAppInquire')}</span>
							</button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* 4. Soft Ambient Auto-Prompt Callout on Page Entry */}
			<AnimatePresence>
				{hasPrompted && !isOpen && (
					<motion.div
						initial={{ opacity: 0, x: 20, scale: 0.95 }}
						animate={{ opacity: 1, x: 0, scale: 1 }}
						exit={{ opacity: 0, x: 20, scale: 0.95 }}
						className='hidden sm:flex items-center space-x-3 absolute bottom-2 right-16 bg-white border border-earth-sand/30 shadow-xl py-2.5 px-4 rounded-xl w-64 text-left pointer-events-none hover:opacity-95 transition-opacity'
					>
						<div className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0'></div>
						<div>
							<p className='font-serif text-[11px] font-bold text-brand-forest leading-none'>
								{t('safariPlannerOnline')}
							</p>
							<p className='font-sans text-[9px] text-brand-charcoal-light mt-1 leading-tight font-light'>
								{t('promptText')}
							</p>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
